import {
  remapMangledThreatChecklistKeys,
  type ResourceSet,
  type ResourceThreatChecklists,
} from 'components/IAP/hooks/resourceSetHooks';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  resolveResourceAutomationRuleChecklists,
  resourceHasAutomationRule,
  type AutomationRuleTypeRef,
} from './automationRuleChecklists';

export type ResourceWithThreatChecklists = Pick<
  ResourceSet,
  | 'threats'
  | 'threat_checklists'
  | 'automation_rules'
  | 'automation_rule_checklists'
>;

/**
 * Checklist items on a resource for a rule name (or legacy threat/category
 * key). Remaps snake_case keys so they match stored names.
 */
function normalizeThreatKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '');
}

function itemsForKey(
  checklists: ResourceThreatChecklists | undefined,
  keys: string[] | undefined,
  needle: string
): OrgEventChecklistItemDef[] {
  const trimmed = needle.trim();
  if (!trimmed) return [];
  const remapped = remapMangledThreatChecklistKeys(checklists, [
    ...(keys ?? []),
    trimmed,
  ]);
  const exact = remapped[trimmed];
  if (Array.isArray(exact) && exact.length > 0) return exact;
  const normalized = normalizeThreatKey(trimmed);
  for (const [key, items] of Object.entries(remapped)) {
    if (!Array.isArray(items) || items.length === 0) continue;
    if (normalizeThreatKey(key) === normalized) return items;
  }
  return Array.isArray(exact) ? exact : [];
}

export function threatChecklistItemsForCategory(
  checklists: ResourceThreatChecklists | undefined,
  threats: string[] | undefined,
  category: string
): OrgEventChecklistItemDef[] {
  return itemsForKey(checklists, threats, category);
}

export function resourceHasChecklistForCategory(
  resource: ResourceWithThreatChecklists,
  category: string,
  rules?: AutomationRuleTypeRef[]
): boolean {
  const resolved = resolveResourceAutomationRuleChecklists(resource, rules);
  if (
    itemsForKey(
      resolved.automationRuleChecklists,
      resolved.automationRules,
      category
    ).length > 0
  ) {
    return true;
  }
  return (
    threatChecklistItemsForCategory(
      resource.threat_checklists,
      resource.threats,
      category
    ).length > 0
  );
}

export function filterResourcesWithChecklistForCategory<
  T extends ResourceWithThreatChecklists,
>(resources: T[], category: string, rules?: AutomationRuleTypeRef[]): T[] {
  return resources.filter((resource) =>
    resourceHasChecklistForCategory(resource, category, rules)
  );
}

/** Assigned to this rule, including an empty starter checklist. */
export function filterResourcesAssignedToAutomationRule<
  T extends ResourceWithThreatChecklists,
>(resources: T[], ruleName: string, rules?: AutomationRuleTypeRef[]): T[] {
  return resources.filter((resource) =>
    resourceHasAutomationRule(resource, ruleName, rules)
  );
}

export function threatChecklistCoverage(
  resource: ResourceWithThreatChecklists,
  rules?: AutomationRuleTypeRef[]
): { covered: number; total: number; percent: number } {
  const resolved = resolveResourceAutomationRuleChecklists(resource, rules);
  const names = (
    resolved.automationRules.length
      ? resolved.automationRules
      : (resource.threats ?? [])
  ).filter((name) => name.trim().length > 0);
  const total = names.length;
  const covered = names.filter((name) =>
    resourceHasChecklistForCategory(resource, name)
  ).length;
  return {
    covered,
    total,
    percent: total === 0 ? 0 : Math.round((covered / total) * 100),
  };
}
